import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookMarked, FileText, Layers, FolderTree, Users, Sparkles, Library, ShieldCheck, Search } from "lucide-react";
import NavBar from "@/library/components/NavBar";
import Footer from "@/library/components/Footer";
import { api } from "@/library/lib/api";
import { useSettings, resolveLogoUrl } from "@/library/lib/settings";

const fade = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 } };

const StatCard = ({ icon: Icon, value, label, testid }) => (
  <motion.div {...fade} transition={{ duration: 0.4 }} data-testid={testid} className="premium-card p-6 text-center">
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand/10 to-gold/10 flex items-center justify-center mx-auto mb-3">
      <Icon className="w-6 h-6 text-brand" />
    </div>
    <div className="font-heading text-3xl md:text-4xl font-extrabold text-brand-dark tabular-nums">{value}</div>
    <div className="text-sm text-slate-500 mt-1 font-medium">{label}</div>
  </motion.div>
);

const StageCard = ({ level, idx }) => {
  const colors = [
    { bar: "from-blue-400 to-brand", icon: "bg-blue-50 text-brand" },
    { bar: "from-gold to-gold-dark", icon: "bg-amber-50 text-gold-dark" },
    { bar: "from-emerald-400 to-emerald-700", icon: "bg-emerald-50 text-emerald-700" },
  ][idx % 3];
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 * idx }}
      data-testid={`stage-card-${level.id}`}
    >
      <Link to={`/library/levels/${level.id}`} className="premium-card group block p-8 h-full relative overflow-hidden">
        <div className={`absolute top-0 inset-x-0 h-1.5 bg-gradient-to-l ${colors.bar}`} />
        <div className={`w-16 h-16 rounded-2xl ${colors.icon} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
          <Library className="w-8 h-8" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-brand-dark mb-2">{level.name}</h3>
        <p className="text-slate-600 leading-relaxed mb-5 text-sm">{level.description || "ملفات ومواد رسمية للمرحلة"}</p>
        <div className="flex items-center gap-2 text-gold-dark font-bold text-sm group-hover:gap-3 transition-all">
          <span>استعراض المرحلة</span>
          <ArrowLeft className="w-4 h-4" />
        </div>
      </Link>
    </motion.div>
  );
};

const Home = () => {
  const [stats, setStats] = useState({ files: 0, indicators: 0, domains: 0, users: 0 });
  const [levels, setLevels] = useState([]);
  const s = useSettings();
  const logoUrl = resolveLogoUrl(s.logo_url);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/levels").then((r) => setLevels(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen flex flex-col">
      <NavBar />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div
          className="absolute inset-0 opacity-20 mix-blend-overlay"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/32634575/pexels-photo-32634575.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-gold/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-brand-light/30 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div {...fade} transition={{ duration: 0.6 }} className="max-w-3xl">
            {logoUrl && (
              <div className="inline-block bg-white rounded-2xl p-4 mb-6 shadow-2xl shadow-black/30">
                <img src={logoUrl} alt="logo" className="w-20 h-20 object-contain" data-testid="hero-logo" />
              </div>
            )}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 text-white/90 text-xs font-semibold mb-6 ms-3">
              <ShieldCheck className="w-3.5 h-3.5 text-gold-light" />
              {s.welcome_badge}
            </div>
            <h1 data-testid="hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5">
              {s.hero_title}
              <span className="block mt-2 text-gold-light text-3xl sm:text-4xl lg:text-5xl">{s.hero_subtitle}</span>
            </h1>
            <div className="gold-line mb-6"></div>
            <p className="text-white/85 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl">
              {s.hero_description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/library/levels" data-testid="hero-cta-enter" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-white font-bold py-3.5 px-8 rounded-xl shadow-2xl shadow-gold/30 transition-all hover:translate-y-[-2px]">
                <BookMarked className="w-5 h-5" /> دخول المكتبة
              </Link>
              <Link to="/library/levels" data-testid="hero-cta-browse" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 font-bold py-3.5 px-8 rounded-xl transition-all">
                <Layers className="w-5 h-5" /> استعراض المراحل
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard testid="stat-files" icon={FileText} value={stats.files} label="ملف PDF" />
          <StatCard testid="stat-indicators" icon={Sparkles} value={stats.indicators} label="مؤشر" />
          <StatCard testid="stat-domains" icon={FolderTree} value={stats.domains} label="مجال" />
          <StatCard testid="stat-users" icon={Users} value={stats.users} label="مستخدم إداري" />
        </div>
      </section>

      {/* Stages */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="text-gold-dark font-bold text-sm tracking-widest mb-3 uppercase">المراحل التعليمية</div>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-brand-dark">اختر المرحلة للبدء</h2>
          <div className="gold-line mx-auto mt-4"></div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {levels.map((l, i) => <StageCard key={l.id} level={l} idx={i} />)}
          {levels.length === 0 && (
            <div className="col-span-3 text-center py-16 text-slate-500">لم تتم إضافة مراحل بعد.</div>
          )}
        </div>
      </section>

      {/* Features strip */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-3 gap-8">
          {[
            { icon: Search, t: "بحث وفلترة دقيقة", d: "ابحث في كافة الملفات وفلترها حسب المرحلة والمجال والمؤشر." },
            { icon: ShieldCheck, t: "أرشفة مؤسسية", d: "تنظيم هرمي رسمي يضاهي أنظمة الوزارات التعليمية." },
            { icon: FileText, t: "عرض PDF داخلي", d: "افتح أي ملف داخل المنصة دون الحاجة لروابط خارجية." },
          ].map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }} className="flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand/8 text-brand flex items-center justify-center shrink-0"><f.icon className="w-6 h-6" /></div>
              <div>
                <div className="font-heading font-bold text-brand-dark mb-1">{f.t}</div>
                <div className="text-sm text-slate-600 leading-relaxed">{f.d}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
